import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Spin, Empty, Table, Tag, Alert, Descriptions } from 'antd';
import moment from 'moment';

const API = process.env.REACT_APP_API_URL;

const STATUS_CONFIG = {
  to_be_cleaned: { label: 'TO BE CLEANED', color: 'orange' },
  cleaned: { label: 'CLEANED', color: 'green' },
  in_use: { label: 'IN USE', color: 'blue' },
  inactive: { label: 'INACTIVE', color: 'default' }
};

const ACTION_LABELS = {
  label_created: 'Label Generated',
  marked_cleaned: 'Marked as Cleaned',
  assigned_to_batch: 'Assigned to Batch',
  sent_for_cleaning: 'Sent for Cleaning',
  sieve_screen_inactivated: 'Inactivated',
  sieve_screen_reactivated: 'Reactivated'
};

const getStatusKey = (record) => {
  if (!record) return 'to_be_cleaned';
  if (record.is_retired) return 'inactive';
  if (record.status === 'batch_processing') return 'in_use';
  if (record.is_cleaned) return 'cleaned';
  return 'to_be_cleaned';
};

const fmtTs = (ts) =>
  ts
    ? moment(ts).utcOffset('+05:30').format('DD MMM YYYY HH:mm') + ' IST'
    : '—';

const parseDetails = (v) => {
  if (!v) return {};
  return typeof v === 'string' ? JSON.parse(v) : v;
};

const SectionTitle = ({ children }) => (
  <h4
    style={{
      fontSize: 11,
      fontWeight: 700,
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
      margin: '16px 0 8px'
    }}
  >
    {children}
  </h4>
);

const logColumns = [
  {
    title: 'Timestamp (IST)',
    dataIndex: 'timestamp',
    key: 'timestamp',
    width: 190,
    render: (v) =>
      v
        ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm:ss') + ' IST'
        : '—'
  },
  {
    title: 'Activity',
    dataIndex: 'action',
    key: 'action',
    width: 170,
    render: (v) => ACTION_LABELS[v] || v
  },
  {
    title: 'By',
    dataIndex: 'performed_by_name',
    key: 'performed_by_name',
    width: 150
  },
  {
    title: 'Details',
    dataIndex: 'details',
    key: 'details',
    render: (v, record) => {
      if (record.action === 'assigned_to_batch' && v) {
        const d = parseDetails(v);
        return (
          <span>
            {[d.product_batch_number, d.product_name, d.process]
              .filter(Boolean)
              .join(' · ')}
          </span>
        );
      }
      if (record.action === 'sieve_screen_inactivated' && v) {
        const d = parseDetails(v);
        return d.reason || '—';
      }
      if (record.action === 'sieve_screen_reactivated' && v) {
        const d = parseDetails(v);
        return d.note || '—';
      }
      return '—';
    }
  }
];

const SievePublicPage = () => {
  const { id } = useParams();
  const [sieve, setSieve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError('');
    fetch(`${API}/sieve-labels-public/${encodeURIComponent(id)}`)
      .then((r) => r.json())
      .then((res) => {
        if (res?.success && res.data) setSieve(res.data);
        else setError(res?.message || `No sieve/screen found for "${id}".`);
      })
      .catch(() => setError('Could not reach the server.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" tip="Loading sieve/screen details..." />
      </div>
    );
  }

  if (error || !sieve) {
    return (
      <Card style={{ maxWidth: 600, margin: '40px auto' }}>
        <Empty description={error || `No sieve/screen found for "${id}".`} />
      </Card>
    );
  }

  const statusKey = getStatusKey(sieve);
  const statusCfg = STATUS_CONFIG[statusKey];
  const logs = sieve.activity_logs || [];
  const isInUse = statusKey === 'in_use';
  const isInactive = statusKey === 'inactive';
  const itemLabel = sieve.item_type || 'Sieve/Screen';

  const lastBatchDetails = sieve.last_batch_info
    ? parseDetails(sieve.last_batch_info.details)
    : null;
  const inactivationDetails = sieve.inactivation_info
    ? parseDetails(sieve.inactivation_info.details)
    : null;

  return (
    <div style={{ maxWidth: 760, margin: '24px auto', padding: '0 12px' }}>
      <Card>
        {/* Decommissioned banner */}
        {isInactive && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={`This ${itemLabel.toLowerCase()} has been decommissioned.`}
            description={
              <span>
                <strong>Reason:</strong>{' '}
                {inactivationDetails?.reason || sieve.retire_reason || '—'}
                &nbsp;&nbsp;
                <strong>Date:</strong>{' '}
                {fmtTs(sieve.inactivation_info?.timestamp || sieve.retired_at)}
              </span>
            }
          />
        )}

        {/* Sieve/Screen identifier */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div
            style={{
              fontFamily: 'Courier New, monospace',
              fontSize: 28,
              fontWeight: 700,
              color: '#c91761',
              letterSpacing: 2
            }}
          >
            {sieve.sieve_label_number}
          </div>
          <Tag color={statusCfg.color} style={{ marginTop: 8, fontSize: 13 }}>
            {statusCfg.label}
          </Tag>
        </div>

        {/* Sieve/Screen Details */}
        <SectionTitle>{itemLabel} Details</SectionTitle>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Label Number">
            <span style={{ fontFamily: 'monospace' }}>
              {sieve.sieve_label_number}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            {sieve.item_type || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="In-House Number">
            {sieve.inhouse_number || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusCfg.color}>{statusCfg.label}</Tag>
          </Descriptions.Item>
        </Descriptions>

        {/* Current Batch (in_use only) */}
        {isInUse && (
          <>
            <SectionTitle>Current Batch</SectionTitle>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Batch Number">
                {sieve.product_batch_number || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Product Name">
                {sieve.product_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Process">
                {sieve.process || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Next Stage">
                {sieve.nextstage || '—'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        {/* Last Batch (not in_use) */}
        {!isInUse && (
          <>
            <SectionTitle>Last Batch</SectionTitle>
            {lastBatchDetails ? (
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Batch Number">
                  {lastBatchDetails.product_batch_number || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Product Name">
                  {lastBatchDetails.product_name || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Process">
                  {lastBatchDetails.process || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Next Stage">
                  {lastBatchDetails.nextstage || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Assigned At">
                  {fmtTs(sieve.last_batch_info?.timestamp)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Empty
                description={`No previous batch found for this ${itemLabel.toLowerCase()}.`}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </>
        )}

        {/* Last Cleaning Details (in_use only) */}
        {isInUse && (
          <>
            <SectionTitle>Last Cleaning Details</SectionTitle>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Cleaned By">
                {sieve.last_cleaning_info?.performed_by_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Cleaned At">
                {fmtTs(sieve.last_cleaning_info?.timestamp)}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        {/* Creation Details */}
        <SectionTitle>Creation Details</SectionTitle>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Created By">
            {sieve.creation_info?.performed_by_name || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {fmtTs(sieve.creation_info?.timestamp)}
          </Descriptions.Item>
        </Descriptions>

        {/* Full Activity History */}
        <SectionTitle>Activity History</SectionTitle>
        <Table
          columns={logColumns}
          dataSource={logs}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                description={`No activity recorded for this ${itemLabel.toLowerCase()} yet.`}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )
          }}
        />
      </Card>
    </div>
  );
};

export default SievePublicPage;
